<script setup>

import { ref, reactive, computed, onMounted } from 'vue';

import { useAppStore } from 'store/store';
const { countries, devices, browsers, operatingSystems } = useAppStore();

import use$T from 'cfg/useTranslation';
const $t = use$T();

import MultiSelect from 'Common/MultiSelect/MultiSelect.vue';

const adFormats = [
  { value: 'CLASSIC_PUSH', label: 'Classic Push' },
  { value: 'IN_PAGE_PUSH', label: 'In-Page Push' },
  { value: 'NATIVE_CPC',   label: 'Native CPC' },
  { value: 'NATIVE_CPM',   label: 'Native CPM' },
  { value: 'BANNER_CPM',   label: 'Banner CPM' },
  { value: 'POP',          label: 'Popunder' },
];

const trafficTypes = computed(() => [
  { value: 'ADULT',      label: $t('@filters.adult') },
  { value: 'MAINSTREAM', label: $t('@filters.mainstream') },
]);

const breakdowns = computed(() => {
  const list = [
    { value: 'OS',      label: $t('@breakdowns.os') },
    { value: 'BROWSER', label: $t('@breakdowns.browser') },
    { value: 'COUNTRY', label: $t('@breakdowns.country') },
  ];
  if (isBanner.value) list.push({ value: 'BANNER_DIMENSION', label: $t('@breakdowns.banner') });
  return list;
});

/* ── Filter state ── */
const sel = reactive({
  countries : [],
  deviceIds : [],
  osIds     : [],
  browserIds: [],
  trafficType      : '',
  adFormat         : '',   // raw select value (CLASSIC_PUSH, NATIVE_CPC…)
  breakdown : 'COUNTRY',
});

const isBanner = computed(() => ['BANNER_CPC', 'BANNER_CPM'].includes(sel.adFormat));

/* ── Query / sorting / paging ── */
const page       = ref(0);
const size       = 50;
const totalPages = ref(0);
const rows       = ref([]);
const totals     = ref(null);
const loading    = ref(false);
const sortBy     = ref('');
const sortDir    = ref('');   // '', 'DESC', 'ASC'

let controller = null;

function toArr(v) { return Array.isArray(v) && v.length ? v : undefined; }

/* Map the raw adFormat select value → API trafficSourceType + bidType */
function resolveSource(value) {
  if (!value) return { trafficSourceType: undefined, bidType: undefined };
  if (['NATIVE_CPC', 'NATIVE_CPM'].includes(value)) {
    return { trafficSourceType: 'NATIVE', bidType: value === 'NATIVE_CPC' ? 'CPC' : 'CPM' };
  }
  if (['BANNER_CPC', 'BANNER_CPM'].includes(value)) {
    return { trafficSourceType: 'BANNER', bidType: value === 'BANNER_CPC' ? 'CPC' : 'CPM' };
  }
  return { trafficSourceType: value, bidType: undefined };
}

const breakdownColLabel = computed(() => {
  switch (sel.breakdown) {
    case 'BROWSER'          : return $t('@columns.browser');
    case 'OS'               : return $t('@columns.os');
    case 'BANNER_DIMENSION' : return $t('@columns.banner');
    default                 : return $t('@columns.country');
  }
});

const isPop = computed(() => sel.adFormat === 'POP');

function buildParams() {
  const { trafficSourceType, bidType } = resolveSource(sel.adFormat);
  const p = {
    page,
    size,
    countries         : toArr(sel.countries),
    deviceIds         : toArr(sel.deviceIds),
    osIds             : toArr(sel.osIds),
    browserIds        : toArr(sel.browserIds),
    trafficType       : sel.trafficType || undefined,
    trafficSourceType,
    bidType,
    breakdown         : sel.breakdown,
    sortBy            : sortBy.value || undefined,
    sortDirection     : sortDir.value || undefined,
  };
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    qs.set(k, k === 'page' ? String(page.value) : String(v));
  });
  return qs.toString();
}

async function fetchData() {
  loading.value = true;
  if (controller) controller.abort();
  controller = new AbortController();

  try {
    const res = await fetch(`https://api.mondiad.com/api/1.0/inventory/traffic?${buildParams()}`, {
      method: 'GET',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    rows.value       = json.data || [];
    totals.value     = json.meta ? json.meta.total : null;
    totalPages.value = json.meta ? json.meta.totalPages : 0;
  } catch (e) {
    if (e.name !== 'AbortError') { rows.value = []; totals.value = null; totalPages.value = 0; }
  } finally {
    loading.value = false;
  }
}


/* ── Reactions ── */
function onFilterChange() {
  page.value = 0;
  fetchData();
}

function onAdFormatChange() {
  // Banner enables the banner-dimension breakdown; leaving banner resets it.
  if (!isBanner.value && sel.breakdown === 'BANNER_DIMENSION') {
    sel.breakdown = 'COUNTRY';
  }
  // Pop has no clicks/CPC-based sort semantics for some columns — reset sort defensively.
  if (isPop.value && ['CLICKS', 'AVG_CPC'].includes(sortBy.value)) {
    sortBy.value = ''; sortDir.value = '';
  }
  onFilterChange();
}

function sort(column) {
  if (sortBy.value === column) {
    sortDir.value = sortDir.value === 'DESC' ? 'ASC' : (sortDir.value === 'ASC' ? '' : 'DESC');
  } else {
    sortDir.value = 'DESC';
  }
  sortBy.value = sortDir.value ? column : '';
  fetchData();
}

function sortState(column) {
  if (sortBy.value !== column || !sortDir.value) return '';
  return sortDir.value;
}

function goPage(n) {
  if (n < 0 || n >= totalPages.value || n === page.value) return;
  page.value = n;
  fetchData();
}

const pageNumbers = computed(() => Array.from({ length: totalPages.value }, (_, i) => i));

function fmt(n) {
  const parts = String(n).split('.');
  return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + (parts[1] ? '.' + parts[1] : '');
}

function money(n) { return '$ ' + Number(n || 0).toFixed(4); }

function breakdownCell(row) {
  switch (sel.breakdown) {
    case 'BROWSER'          : return row.browser;
    case 'OS'               : return row.os;
    case 'BANNER_DIMENSION' : return row.bannerDimension;
    default                 : return row.country;
  }
}

onMounted(()=> fetchData());
</script>

<template>
  <div class="ti-app">
    <div class="ti-filters">
      <MultiSelect multiple search :options="countries"  v-model="sel.countries"  :title="$t('@filters.countries')" @change="onFilterChange" label="name" value="id" />
      <MultiSelect :options="adFormats" v-model="sel.adFormat" :title="$t('@filters.ad_formats')" @change="onAdFormatChange" />
      <MultiSelect multiple search :options="devices"    v-model="sel.deviceIds"  :title="$t('@filters.devices')"  @change="onFilterChange" label="name" value="id" />
      <MultiSelect multiple search :options="operatingSystems"       v-model="sel.osIds"      :title="$t('@filters.oses')"     @change="onFilterChange" label="name" value="id" />
      <MultiSelect multiple search :options="browsers"   v-model="sel.browserIds" :title="$t('@filters.browsers')" @change="onFilterChange" label="name" value="id" />
      <MultiSelect :options="trafficTypes" v-model="sel.trafficType" :title="$t('@filters.traffic')" @change="onFilterChange" />
      <MultiSelect :options="breakdowns"   v-model="sel.breakdown"   :title="$t('@filters.breakdown')" @change="onFilterChange" />
    </div>

    <div class="ti-table-wrap">
      <div v-if="loading" class="ti-loader"><span class="ti-spinner"></span></div>

      <table class="ti-table" :class="{ 'is-pop': isPop }">
        <thead>
          <tr>
            <th class="ti-th ti-th-breakdown" @click="sort(sel.breakdown)">
              <span>{{ breakdownColLabel }}</span>
              <span class="ti-sort" :data-dir="sortState(sel.breakdown)"></span>
            </th>
            <th class="ti-th" @click="sort('IMPRESSIONS')">
              <span>{{ $t('@columns.impressions') }}</span>
              <span class="ti-sort" :data-dir="sortState('IMPRESSIONS')"></span>
            </th>
            <th class="ti-th ti-th-clicks" @click="sort('CLICKS')">
              <span>{{ $t('@columns.clicks') }}</span>
              <span class="ti-sort" :data-dir="sortState('CLICKS')"></span>
            </th>
            <th class="ti-th" @click="sort('AVG_CPM')">
              <span>{{ $t('@columns.cpm') }}</span>
              <span class="ti-sort" :data-dir="sortState('AVG_CPM')"></span>
            </th>
            <th class="ti-th ti-th-cpc" @click="sort('AVG_CPC')">
              <span>{{ $t('@columns.cpc') }}</span>
              <span class="ti-sort" :data-dir="sortState('AVG_CPC')"></span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in rows" :key="i">
            <td class="ti-td ti-td-breakdown">
              <span v-if="sel.breakdown === 'COUNTRY'" class="ti-country">
                <img v-if="row.countryCode" :src="'https://mondiad.com/images/flags/' + row.countryCode.toLowerCase() + '.svg'" :alt="row.country" class="ti-flag" @error="$event.target.style.display='none'">
                <span>{{ row.country }}</span>
              </span>
              <span v-else>{{ breakdownCell(row) }}</span>
            </td>
            <td class="ti-td">{{ fmt(row.impressions) }}</td>
            <td class="ti-td ti-td-clicks">{{ fmt(row.clicks) }}</td>
            <td class="ti-td">{{ money(row.avgCpm) }}</td>
            <td class="ti-td ti-td-cpc">{{ money(row.avgCpc) }}</td>
          </tr>
          <tr v-if="totals && rows.length" class="ti-total-row">
            <td class="ti-td ti-td-breakdown">{{ $t('@total') }}</td>
            <td class="ti-td">{{ fmt(totals.impressions) }}</td>
            <td class="ti-td ti-td-clicks">{{ fmt(totals.clicks) }}</td>
            <td class="ti-td">{{ money(totals.avgCpm) }}</td>
            <td class="ti-td ti-td-cpc">{{ money(totals.avgCpc) }}</td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loading && !rows.length" class="ti-nodata">{{ $t('@no_data') }}</div>
    </div>

    <nav v-if="totalPages > 1" class="ti-pagination">
      <button class="ti-page-btn" :disabled="page === 0" @click="goPage(page - 1)">‹</button>
      <button
        v-for="n in pageNumbers"
        :key="n"
        class="ti-page-btn"
        :class="{ active: n === page }"
        @click="goPage(n)"
      >{{ n + 1 }}</button>
      <button class="ti-page-btn" :disabled="page === totalPages - 1" @click="goPage(page + 1)">›</button>
    </nav>

    <p class="ti-tip">{{ $t('@time_tip') }}</p>
  </div>
</template>
